import React, { useState, useRef, useEffect } from 'react';
import ReactionTooltip from './ReactionTooltip';

const API_BASE = process.env.REACT_APP_API_BASE;
const API_KEY = process.env.REACT_APP_API_KEY;

const EMOJI_OPTIONS = ['😍', '🔥', '👏', '💯', '😱', '😂', '💩', '🤌', '👎'];

export default function ReactionBar({ reviewId, reactions, totalReactions, userReaction, user, onReactionUpdate }) {
  const [isLoading, setIsLoading] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const pickerRef = useRef(null);
  const buttonRef = useRef(null);

  // Close picker when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (pickerRef.current && !pickerRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleReactionClick = async (emoji) => {
    if (!user) {
      // Could show login prompt here
      return;
    }

    setShowEmojiPicker(false); // Close picker after selection
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/v1/reviews/${reviewId}/reactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY,
          'X-User-ID': user.id,
        },
        body: JSON.stringify({ emoji }),
      });

      if (response.ok) {
        const result = await response.json();

        // Update the reactions optimistically
        const newReactions = { ...reactions };
        const newUserReaction = result.action === 'added' ? emoji : null;

        if (result.action === 'added') {
          newReactions[emoji] = (newReactions[emoji] || 0) + 1;
        } else {
          newReactions[emoji] = Math.max((newReactions[emoji] || 1) - 1, 0);
          if (newReactions[emoji] === 0) {
            delete newReactions[emoji];
          }
        }

        const newTotal = Object.values(newReactions).reduce((sum, count) => sum + count, 0);

        // Notify parent component of the update
        if (onReactionUpdate) {
          onReactionUpdate(reviewId, {
            reactions: newReactions,
            total_reactions: newTotal,
            user_reaction: newUserReaction
          });
        }
      }
    } catch (error) {
      console.error('Failed to toggle reaction:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
      {/* Existing reactions */}
      {Object.entries(reactions).map(([emoji, count]) => (
        <ReactionTooltip
          key={emoji}
          reviewId={reviewId}
          emoji={emoji}
          user={user}
        >
          <button
            onClick={() => handleReactionClick(emoji)}
            disabled={isLoading || !user}
            className={`
              flex items-center gap-1 px-2 py-1 rounded-full text-sm
              ${userReaction === emoji
                ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }
              ${!user ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              transition-colors duration-200
            `}
          >
            <span className="text-base">{emoji}</span>
            <span className="font-medium">{count}</span>
          </button>
        </ReactionTooltip>
      ))}

      {/* Add new reaction dropdown */}
      {user && (
        <div className="relative">
          <button
            ref={buttonRef}
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            disabled={isLoading}
            className={`
              flex items-center justify-center w-8 h-8 rounded-full
              transition-colors duration-200
              ${showEmojiPicker
                ? 'bg-indigo-100 text-indigo-700'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800'
              }
            `}
          >
            <span className="text-lg">+</span>
          </button>

          {/* Emoji picker - click to show/hide */}
          {showEmojiPicker && (
            <div
              ref={pickerRef}
              className="
                absolute bottom-full left-0 mb-1
                bg-white border border-gray-200 rounded-lg shadow-lg p-2 flex gap-1
                z-20
              "
            >
              {EMOJI_OPTIONS.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => handleReactionClick(emoji)}
                  disabled={isLoading}
                  className="
                    w-8 h-8 rounded hover:bg-gray-100 flex items-center justify-center
                    text-lg transition-colors duration-200
                  "
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Total count indicator */}
      {totalReactions > 0 && (
        <span className="text-xs text-gray-500 ml-auto">
          {totalReactions} reaction{totalReactions !== 1 ? 's' : ''}
        </span>
      )}
    </div>
  );
}